import type { SourcePreviewOptions, SourcePreviewSchemaField, UpdateDatasetOptions } from "@prequel/import-react";

import type { DatasetMethod } from "./types";

export function buildPreviewRequest(
  method: DatasetMethod,
  sourceTable: string,
  sqlQuery: string,
): SourcePreviewOptions | undefined {
  if (method === "sql") {
    const query = sqlQuery.trim();
    return query ? { method: "sql", sql: { query } } : undefined;
  }

  const table = parseQualifiedTable(sourceTable);
  if (!table) {
    return undefined;
  }

  return { method: "table", table };
}

function parseQualifiedTable(value: string) {
  const table = value.trim();
  const splitIndex = table.lastIndexOf(".");
  if (splitIndex <= 0 || splitIndex === table.length - 1) {
    return undefined;
  }

  return {
    source_table_namespace: table.slice(0, splitIndex),
    source_table_name: table.slice(splitIndex + 1),
  };
}

export function getPreviewRequestKey(request: SourcePreviewOptions) {
  if (request.method === "sql") {
    return `sql:${request.sql.query}`;
  }

  if (request.method === "table") {
    return `table:${request.table.source_table_namespace}.${request.table.source_table_name}`;
  }

  return `glob:${request.glob.pattern}:${request.glob.format}`;
}

export function getFrequencyLabel(minutes: number) {
  switch (minutes) {
    case 60:
      return "1 hour";
    case 360:
      return "6 hours";
    case 720:
      return "12 hours";
    case 1440:
      return "1 day";
    default:
      return `${minutes} minutes`;
  }
}

export function isColumnType(field: SourcePreviewSchemaField, type: "timestamp" | "boolean") {
  const typeText = `${field.prequel_type ?? ""} ${field.raw_type ?? ""}`.toLowerCase();
  if (type === "boolean") {
    return typeText.includes("bool");
  }
  return typeText.includes("timestamp") || typeText.includes("datetime") || typeText.includes("date");
}

// Source data is fixed once a dataset exists, so an update only carries the editable fields.
export function buildUpdateDatasetOptions({
  name,
  frequencyMinutes,
  isEnabled,
}: {
  name: string;
  frequencyMinutes: string;
  isEnabled: boolean;
}): UpdateDatasetOptions {
  return {
    ...(name.trim() ? { name: name.trim() } : {}),
    frequency_minutes: Number(frequencyMinutes),
    is_enabled: isEnabled,
  };
}
